import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { FazAksiyon } from './FazAksiyon';
import { FotoEkle } from './FotoEkle';
import { AdimDuzelt } from './AdimDuzelt';
import { useOturum } from '../lib/oturum';
import { GeriDugme, SessizDugme } from '../lib/dugme';
import { FotoSerit } from '../lib/foto-goster';
import { adimDurumu, fazAdimlari, fazSirasi } from '../lib/faz-adimlari';
import {
  NihaiMetrajSecici,
  OlcuListesi,
  metrajGoster,
  nihaiOlculer,
  nihaiToplam,
  type ManuelOlcu,
} from '../lib/olcu';
import type { DuzeltmeAlani, IskeleTipi, JobStatus, OlcuInput, PhotoStep } from '@mod/domain';
import {
  duzeltilebilirMi,
  durumIzinVeriyorMu,
  fotoGecersizIsaretlenebilirMi,
  k5_izinFotosuVarMi,
  SOKUM_GERI_ALINDI,
} from '@mod/domain';
import {
  KameraIkon,
  KilitIkon,
  NoktaIkon,
  NotIkon,
  OnayIkon,
  RevizeIkon,
  SokumIkon,
} from '../lib/ikon';

/**
 * İŞ DETAYI — sistemin kalbi (devir dokumani Bolum 6.3).
 *
 * v9'un en buyuk yeniligi: bir iskelenin TUM OMRU tek ekranda, FAZ KUTULARI halinde.
 * Kurulum fazi her zaman var; revize ve sokum fazlari olustukca altina eklenir.
 *
 * Adimlar AKORDEON: tiklanunca acilir, icerigi gorunur/duzenlenir.
 * K10 — zaman damgasi YALNIZ GERCEKLESMIS adimlarda gosterilir.
 */

interface Detay {
  id: string;
  isEmriNo: string | null;
  tanim: string;
  unite: string;
  /** Ünite düzeltmesi kimlikle gider — ad tekrar edebilir. */
  uniteId: string;
  ilgili: string[];
  /** İKİ AYRI TOPLAM — karışık iş tek sayıya indirgenmez (02.08.2026). */
  metrajM3: number;
  metrajM2: number;
  /** m³ + m² × 4 — tek sayı gerektiğinde. */
  esdegerM3: number;
  /* `string` DEĞİL: durum makinesine (`durumIzinVeriyorMu`) besleniyor ve
     serbest metin olduğunda ekran, olmayan bir duruma göre karar verebiliyordu. */
  durum: JobStatus;
  isEmriFotoVar: boolean;
  yesilKart: boolean;
  /** Teslim damgası — BİR KEZ olur, geri alınamaz. Adım durumunun ölçütü. */
  teslimAt: string | null;
  kuruluGun: number | null;
  kesifci: string | null;
  iskeleOnayNo: string | null;
  /** SAP iş emri alanları — yalnız SOCAR gibi müşterilerde dolu. */
  sorumluIsyeri?: string | null;
  planlamaGrubu?: string | null;
  /** SAP önceliği (1-6) — K6: bilgi, sıralamaya girmez. */
  oncelik?: number | null;
  /** Dış proje bölgesi: K5 izin kâğıdı istenmez. */
  izinGerekmez?: boolean;
  /** HAKEDİŞ VERİSİ — yalnız saklanır, hesap yapılmaz (§9). */
  sokumTalepAt?: string | null;
  hakedisMetraj?: number | null;
  hakedisBirim?: 'm3' | 'm2' | 'insan_saat' | null;
  kesintiYuzde?: number | null;
  kesifNot: string | null;
  ertelemeNedeni: string | null;
  izin: 'alindi' | 'override' | null;
  izinNotu: string | null;
  malzemeSevk: boolean;
  olculer: { ad: string | null; tip?: IskeleTipi | null; en: number | null; boy: number | null; yuk: number | null; yaricap?: number | null }[];
  fazlar: {
    id: string;
    tip: string;
    durum: string | null;
    revizeTur: string | null;
    not: string | null;
    ekip: string[];
    ekipBasi: string | null;
    ekipIds: string[];
    ekipBasiId: string | null;
    basladiAt: string | null;
    bittiAt: string | null;
  }[];
  fotolar: {
    fotoNo: number;
    step: string;
    cekilmeZamani: string;
    contentType?: string | null;
    /** Yanlış yüklenen kâğıt — kayıt durur, sayılmaz (K8). */
    gecersiz?: boolean;
    gecersizNeden?: string | null;
  }[];
  izKaydi: { zaman: string; metin: string; tip: string; temsilMi: boolean }[];
  yetki: { sefYetkisi: boolean };
}

/**
 * `Detay` → K5 kuralının beklediği dilim.
 *
 * ⚠️ Kural `@mod/domain`de; arayüz onu ÇAĞIRIR, kopyalamaz. Kopyalandığı gün
 *    (03.08.2026) sunucu kuralı genişledi, arayüz eski kaldı ve "Onayla &
 *    Teslim" düğmesi AÇIK görünüp sunucudan sessizce reddedildi.
 */
function izinDilimi(d: Detay) {
  return {
    izin: d.izin,
    // Dış proje: kural kâğıt istemez — sunucuyla AYNI veriyle okunur.
    izinGerekmez: d.izinGerekmez,
    photos: d.fotolar.map((f) => ({
      step: f.step as PhotoStep,
      phaseType: 'kurulum' as const,
      cekilmeZamani: new Date(f.cekilmeZamani),
      /*
       * ⚠️ GEÇERSİZ BAYRAĞI İSTEMCİYE DE GEÇER. Sunucu bu kaydı saymıyor;
       *    istemci sayarsa ekran "teslim edilebilir" der, sunucu reddeder ve
       *    kullanıcı sebebi görünmeyen bir duvara toslar. İki taraf AYNI
       *    kuralı AYNI veriyle okumalı.
       */
      gecersiz: f.gecersiz,
    })),
  };
}

const DURUM_ETIKET: Record<string, string> = {
  giris: 'Keşif bekliyor',
  kesif: 'İzin & malzeme bekleniyor',
  hazirlik: 'Kuruluma hazır',
  kurulum: 'Kurulumda',
  kontrol: 'Şef onayı bekliyor',
  ayakta: 'KURULU',
  revize: 'Revizede',
  sokum: 'Sökümde',
  kapali: 'Kapandı',
  ertelendi: 'Ertelendi',
};

/** §3.5 — iş emri arka yüzündeki "yapılmama nedeni" kutuları. */
const NEDENLER = [
  { d: 'personel_yetersiz', e: 'Personel yetersiz' },
  { d: 'zaman_yetersiz', e: 'Zaman yetersiz' },
  { d: 'malzeme_yetersiz', e: 'Malzeme yetersiz' },
  { d: 'is_iptali', e: 'İş iptali' },
  { d: 'isletme_kaynakli', e: 'İşletme kaynaklı' },
  { d: 'diger', e: 'Diğer' },
] as const;
const NEDEN_ETIKET: Record<string, string> = Object.fromEntries(
  NEDENLER.map((n) => [n.d, n.e]),
);

/**
 * İptal linki neden yok? — şefe sessiz bir boşluk yerine sebep gösterilir.
 *
 * ⚠️ İptal "henüz kurulmamış işi kapatmak"tır. İskele bir kez dikildiyse
 *    ortada SAHADA ÇELİK vardır; onu kayıttan silmek değil SÖKMEK gerekir.
 *    Durum makinesindeki `iptal_edildi.from` listesinin gerekçesi budur.
 */
const IPTAL_NEDEN_YOK: Record<string, string> = {
  kontrol:
    'İskele kurulmuş durumda — iptal edilemez. Yanlış kurulduysa “Güvenli değil → kuruluma geri gönder”, hiç durmayacaksa teslim edip söküm açın.',
  ayakta: 'Kurulu iskele iptal edilemez — söküm başlatın.',
  revize: 'Revize sürerken iptal edilemez.',
  // ⚠️ Sökümde "iptal" yok ama YOL var: söküm EMRİ geri alınabilir (Nuh 26.08.2026).
  sokum: 'Söküm sürüyor — iş iptal edilemez. Söküm yanlışlıkla açıldıysa emri geri alın.',
  kapali: 'İş kapandı — iptal edilemez.',
};

const zmn = (s: string | null) =>
  s ? new Date(s).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : null;

export function IsDetay({
  id,
  onGeri,
  /** Geri düğmesinin metni — geldiği ekranın adı (bkz. lib/geri.ts). */
  geriEtiket = 'Pano',
}: {
  id: string;
  onGeri: () => void;
  geriEtiket?: string;
}) {
  const { ben } = useOturum();
  const [d, setD] = useState<Detay | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [acik, setAcik] = useState<string | null>(null);
  /** ⋮ ile açılan düzeltme formu — hangi adım için? */
  const [duzeltAdim, setDuzeltAdim] = useState<DuzeltmeAlani | null>(null);

  /*
   * DUZELTME KAPISI — kural `@mod/domain`den geliyor, burada YENIDEN YAZILMIYOR.
   * CLAUDE.md mimari ilkesi: client butonu sondurur, server istegi reddeder;
   * ayni kural iki yerde ayri yazilmaz.
   */
  const duzeltGate = (alan: DuzeltmeAlani) => {
    if (!ben || !d) return undefined;
    const izin = duzeltilebilirMi(
      { durum: d.durum },
      alan,
      { id: ben.id, siteId: ben.santiye.siteId, role: ben.rol, sistemRol: ben.sistemRol },
    );
    return izin.ok ? () => setDuzeltAdim(alan) : undefined;
  };
  /**
   * "BU YANLIŞ FOTOĞRAF" — yetki varsa şeride eylem düşer, yoksa hiç çizilmez.
   *
   * ⚠️ Kural @mod/domain'de; burada yalnız düğmenin görünürlüğü belirleniyor.
   *    Sunucu isteği bağımsız olarak yeniden doğruluyor (T6/T7).
   */
  const gecersizIsle =
    ben && d && fotoGecersizIsaretlenebilirMi(
      { durum: d.durum },
      { gecersiz: false },
      { id: ben.id, siteId: ben.santiye.siteId, role: ben.rol, sistemRol: ben.sistemRol },
    ).ok
      ? async (fotoNo: number, gerekce: string) => {
          await api.post(`/fotolar/${fotoNo}/gecersiz`, { gerekce });
          await yukle();
        }
      : undefined;

  /** K5 override — kazara basılmasın diye kapalı başlar. */
  const [overrideAcik, setOverrideAcik] = useState(false);
  const [overrideNot, setOverrideNot] = useState('');
  const [erteleAcik, setErteleAcik] = useState<'ertele' | 'iptal' | null>(null);
  const [sokumGeriAlAcik, setSokumGeriAlAcik] = useState(false);
  const [mesgul, setMesgul] = useState(false);

  const yukle = useCallback(async () => {
    try {
      setD(await api.get<Detay>(`/isler/${id}`));
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Yüklenemedi');
    }
  }, [id]);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  const cagir = async (yol: string, govde: Record<string, unknown> = {}) => {
    setHata(null);
    setMesgul(true);
    try {
      await api.post(`/isler/${id}/${yol}`, {
        clientCommandId: crypto.randomUUID(),
        olayZamani: new Date().toISOString(),
        ...govde,
      });
      await yukle();
      setAcik(null);
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'İşlem başarısız');
    } finally {
      setMesgul(false);
    }
  };

  if (hata && !d) {
    return <p className="m-6 rounded-(--radius-mod) bg-crit/10 p-4 text-sm text-crit">{hata}</p>;
  }
  if (!d) return <p className="m-6 text-sm text-wait">Yükleniyor…</p>;

  const kurulum = d.fazlar.find((f) => f.tip === 'kurulum');
  const foto = (step: string) => d.fotolar.filter((f) => f.step === step);
  const izinFotosuEksik = !k5_izinFotosuVarMi(izinDilimi(d)).ok;
  /** Künyedeki ünite "düzelt" bağlantısı — "İş emri" adımının ⋮'süyle aynı kapı. */
  const uniteDuzelt = duzeltGate('isEmri');

  /**
   * ADIM DURUMLARI — prototipteki (onayli referans) dort-durum modeli.
   * Sirasi gelmemis adim `locked`: tiklanamaz, asma kilit gosterir.
   */
  const kesifTamam = d.olculer.length > 0 || !['giris'].includes(d.durum);
  // Dış proje bölgesinde izin sorusu yoktur; adım yalnız malzemeyle tamamlanır.
  const izinMalzemeTamam = (d.izin != null || !!d.izinGerekmez) && d.malzemeSevk;
  /*
   * ⚠️ KURULUMU YALNIZ MALZEME KİLİTLER (03.08.2026). İzin cevapsız kalabilir;
   *    tek kapı teslimde (K5, `k5_izinFotosuVarMi`). Önce izin de arandığı için
   *    kurulum adımı asma kilitli kalıyor, kullanıcı bunu "izin fotoğrafı
   *    yüzünden" sanıyordu.
   */
  const kurulumaHazir = d.malzemeSevk;
  const kurulumBasladi = !!kurulum?.basladiAt;
  const kurulumBitti = !!kurulum?.bittiAt;
  /*
   * ⚠️ ÖLÇÜT `teslimAt`, işin ANLIK DURUMU DEĞİL. Durum listesi sayarken
   *    `sokum` ve `revize` unutuluyordu: teslim edilmiş ama söküme girmiş bir
   *    işte teslim adımı GERİ "bekliyor"a dönüyordu. Teslim BİR KEZ olur ve
   *    geri alınamaz (K2 geçidi geçildi, müşteriye mail gitti, yeşil kart
   *    asıldı) — damgası varsa o adım tamamdır.
   */
  const teslimEdildi = d.teslimAt != null;

  const durumlar: Record<string, AdimDurum> = {
    isemri: d.isEmriFotoVar ? 'done' : 'current',
    kesif: kesifTamam ? 'done' : 'current',
    izin: !kesifTamam ? 'locked' : izinMalzemeTamam ? 'done' : 'current',
    kurulum: !kurulumaHazir ? 'locked' : kurulumBitti ? 'done' : 'current',
    onay: !kurulumBitti ? 'locked' : teslimEdildi ? 'done' : 'current',
  };

  /**
   * SIRADAKI ADIM OTOMATIK ACIK.
   * Prototipteki `_curKey` davranisi: kullanici isi acinca yapmasi gereken adim
   * ZATEN ACIK gelir. §1.4 "minimum tik" ilkesinin somut karsiligi — saha
   * kullanicisi hangi adima basacagini aramaz.
   */
  const sirada =
    ['kesif', 'izin', 'kurulum', 'onay', 'isemri'].find((k) => durumlar[k] === 'current') ?? null;
  const acikAdim = acik === '__kapali__' ? null : (acik ?? sirada);

  return (
    <div className="mx-auto max-w-4xl p-4 lg:p-6">
      <GeriDugme etiket={geriEtiket} onClick={onGeri} />

      {/* İş künyesi */}
      <div className="mb-5 rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <span className="num text-sm text-wait">
              {d.isEmriNo ?? 'iş emri numarası yok'}
              {/* İskele onay no — müşterinin verdiği numara, her işte olmaz. */}
              {d.iskeleOnayNo && <span> · onay no {d.iskeleOnayNo}</span>}
            </span>
            <h1 className="text-lg font-semibold text-navy">{d.tanim}</h1>
            <p className="mt-1 text-sm text-wait">
              {d.unite}
              {/*
                ÜNİTE DÜZELTMESİ KÜNYEDEN DE AÇILIR (09.10.2026, Akçansa): yol
                "İş emri" adımının ⋮ menüsündeydi ve hiç kullanılmadı. Aynı form,
                aynı kapı; kapı kapalıysa bağlantı hiç çizilmez.
              */}
              {uniteDuzelt && (
                <button
                  type="button"
                  onClick={uniteDuzelt}
                  className="ml-1.5 text-xs text-navy underline underline-offset-2"
                >
                  düzelt
                </button>
              )}
              {' '}· İlgili: {d.ilgili.join(', ') || '—'} · Keşif: {d.kesifci ?? '—'}
            </p>
            {(d.sorumluIsyeri || d.planlamaGrubu || d.oncelik) && (
              <p className="mt-0.5 text-xs text-wait">
                Sorumlu işyeri <span className="num">{d.sorumluIsyeri ?? '—'}</span> · Planlama
                grubu <span className="num">{d.planlamaGrubu ?? '—'}</span> · Öncelik{' '}
                <span className="num">{d.oncelik ?? '—'}</span>
              </p>
            )}
          </div>
          <div className="text-right">
            <span className="rounded bg-navy/10 px-2 py-1 text-xs text-navy">
              {DURUM_ETIKET[d.durum] ?? d.durum}
            </span>
            <p className="num mt-1 text-lg font-semibold">
              {metrajGoster(d)}
            </p>
            {d.kuruluGun != null && (
              <p className="num text-xs text-wait">{d.kuruluGun} gündür kurulu</p>
            )}
          </div>
        </div>
        {!d.isEmriFotoVar && (
          <p className="mt-3 rounded-(--radius-mod) bg-warn/10 p-2.5 text-xs text-warn">
            <strong>İş emri kâğıdı fotoğrafı eksik.</strong> Bu yüklenmeden iş kapanamaz (K3).
          </p>
        )}
      </div>

      {hata && (
        <p className="mb-4 rounded-(--radius-mod) bg-crit/10 p-3 text-sm text-crit">{hata}</p>
      )}

      {/*
        ERTELE / YENİDEN PLANLA (§4.5 · §3.5).
        Kâğıt iş emrinin ARKA YÜZÜNDEKİ "yapılmama nedeni" kutularının karşılığı.
        Ertelenen iş dikkat kartı üretmez — sebebi bilinen bekleme, şefin
        "bugün neye bakmalıyım" listesini kirletmemeli.
      */}
      {/*
        İPTAL LİNKİ HER ADIMDA GÖRÜNÜR — ama yalnız ŞEFE (müşteri isteği
        04.08.2026). Daha önce yalnız `giris·keşif·hazırlık·kurulum` durumlarında
        çıkıyordu; `kontrol` adımındaki şef hiçbir yerde iptal yolu bulamıyordu
        ve linkin neden olmadığı da yazmıyordu.

        ⚠️ Durum listesi ARTIK ELLE YAZILMIYOR — durum makinesinden geliyor
           (`durumIzinVeriyorMu`). Elle yazılan liste `ertelendi`yi atlamıştı:
           makine izin veriyordu, ekranda yol yoktu.

        ⚠️ Kurulu iskele İPTAL EDİLEMEZ, sökülmesi gerekir. Bu durumlarda link
           yerine SEBEBİ yazıyoruz — sessiz bir yokluk, kullanıcıya "bu ekranda
           iptal yok" değil "iptal neden yok" bilgisini vermeli.
      */}
      {ben?.sefYetkisi && d.durum !== 'iptal' && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {durumIzinVeriyorMu(d.durum, 'ertelendi') && (
            <SessizDugme onClick={() => setErteleAcik('ertele')} renk="notr">
              Yapılamadı — ertele
            </SessizDugme>
          )}
          {/* İPTAL: erteleme "sonra yapacağız", iptal "yapmayacağız" (30.07 müşteri isteği) */}
          {durumIzinVeriyorMu(d.durum, 'iptal_edildi') ? (
            <SessizDugme onClick={() => setErteleAcik('iptal')} renk="crit">
              İşi iptal et
            </SessizDugme>
          ) : (
            <span className="text-xs text-wait">{IPTAL_NEDEN_YOK[d.durum] ?? ''}</span>
          )}
          {/*
            SÖKÜM EMRİNİ GERİ AL (Nuh 26.08.2026). İptalden AYRI bir eylem:
            iskele yerinde kalır, iş `ayakta`ya döner. Boş alan fotoğrafı
            çekildiyse sunucu reddeder — o noktada iskele fiilen inmiştir.
          */}
          {d.durum === 'sokum' && (
            <SessizDugme onClick={() => setSokumGeriAlAcik(true)} renk="crit">
              Söküm emrini geri al
            </SessizDugme>
          )}
        </div>
      )}
      {ben?.sefYetkisi && ['ertelendi', 'iptal'].includes(d.durum) && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-(--radius-mod) border border-warn/30 bg-warn/5 p-3 text-sm text-warn">
          <span>
            {d.durum === 'iptal' ? 'İş İPTAL edildi' : 'İş ertelendi'}
            {d.ertelemeNedeni ? ` — ${NEDEN_ETIKET[d.ertelemeNedeni] ?? d.ertelemeNedeni}` : ''}.
          </span>
          <button
            onClick={() => void cagir('yeniden-planla')}
            className="rounded-(--radius-mod) bg-navy px-3 py-1.5 text-white"
          >
            Yeniden akışa al
          </button>
        </div>
      )}

      {sokumGeriAlAcik && (
        <SokumGeriAl
          id={d.id}
          onKapat={() => setSokumGeriAlAcik(false)}
          onBitti={() => {
            setSokumGeriAlAcik(false);
            void yukle();
          }}
        />
      )}

      {erteleAcik && (
        <Ertele
          id={d.id}
          mod={erteleAcik}
          onKapat={() => setErteleAcik(null)}
          onBitti={() => {
            setErteleAcik(null);
            void yukle();
          }}
        />
      )}

      {duzeltAdim && (
        <AdimDuzelt
          d={d}
          adim={duzeltAdim}
          onKapat={() => setDuzeltAdim(null)}
          onBitti={() => {
            setDuzeltAdim(null);
            void yukle();
          }}
        />
      )}

      {/* ── FAZ KUTUSU: KURULUM (her işte var) ── */}
      <section className="mb-5 overflow-hidden rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod)">
        <h2 className="border-b border-line bg-ground px-4 py-2.5 text-sm font-semibold text-navy">
          ▣ KURULUM
        </h2>

        <Adim
          k="isemri"
          baslik="İş emri (kâğıt)"
          durum={durumlar.isemri!}
          gate
          zaman={foto('is_emri')[0]?.cekilmeZamani ?? null}
          acik={acikAdim === 'isemri'}
          onAc={() => setAcik(acikAdim === 'isemri' ? '__kapali__' : 'isemri')}
          // §6.3: "Akış sürerken evet; KAPANDIKTAN SONRA HAYIR"
          onDuzelt={
            duzeltGate('isEmri')
          }
        >
          {d.isEmriFotoVar ? (
            /*
             * ⚠️ YÜKLEME YOLU FOTO VARKEN DE AÇIK (06.09.2026, Akçansa):
             *    "iş emri yüklerken sadece 1 sayfa yüklenebiliyor, bugün 2
             *    sayfa olarak geldi." Kâğıt tek sayfa olmak zorunda değil.
             */
            <div className="flex flex-wrap items-center gap-3">
              <FotoSerit
                  fotolar={foto('is_emri')}
                  boyut={64}
                  onGecersiz={gecersizIsle}
                  eylem="yanlışsa değiştir"
                />
              <p className="text-sm text-wait">
                {foto('is_emri').length > 1
                  ? `Kâğıt yüklendi (${foto('is_emri').length} sayfa)`
                  : 'Kâğıt fotoğrafı yüklendi'}
              </p>
              <FotoEkle
                jobId={d.id}
                step="is_emri"
                etiket="Sayfa ekle"
                gorunum="kompakt"
                onBitti={yukle}
              />
            </div>
          ) : (
            <div className="space-y-2">
              {/*
                ⚠️ GEÇERSİZ İŞARETLENENLER YİNE GÖRÜNÜR. `isEmriFotoVar` false
                   olduğunda (tek kâğıt geçersiz işaretlenmişse) şerit hiç
                   çizilmeseydi, kayıt kullanıcının gözünde SİLİNMİŞ olurdu —
                   oysa sistemin sözü "silinmez, sayılmaz".
              */}
              {foto('is_emri').length > 0 && (
                <FotoSerit
                  fotolar={foto('is_emri')}
                  boyut={64}
                  onGecersiz={gecersizIsle}
                  eylem="yanlışsa değiştir"
                />
              )}
              <FotoEkle
                jobId={d.id}
                step="is_emri"
                etiket="İş emri fotoğrafı ekle"
                gorunum="kompakt"
                onBitti={yukle}
              />
            </div>
          )}
        </Adim>

        <Adim
          k="kesif"
          baslik="Keşif + kurulum ekibi"
          durum={durumlar.kesif!}
          zaman={null}
          acik={acikAdim === 'kesif'}
          onAc={() => setAcik(acikAdim === 'kesif' ? '__kapali__' : 'kesif')}
          // §6.3: "KAPALI İŞTE BİLE EVET — audit'e işlenir" (kullanıcı özel isteği)
          onDuzelt={duzeltGate('kesif')}
        >
          {d.olculer.length > 0 ? (
            <div className="space-y-1 text-sm">
              <OlcuListesi olculer={d.olculer} m3={d.metrajM3} m2={d.metrajM2} />
              {d.kesifNot && <p className="mt-2 text-wait">Not: {d.kesifNot}</p>}
              <p className="mt-2 text-wait">
                Ekip: {kurulum?.ekip.join(', ') || 'seçilmedi'}
              </p>
              {/* Keşif fotoğrafları — kanıt burada da görünür olmalı */}
              {foto('kesif').length > 0 && (
                <div className="mt-3">
                  <p className="mb-1 text-xs text-wait">Keşif fotoğrafları</p>
                  <div className="flex justify-start">
                    <FotoSerit fotolar={foto('kesif')} onGecersiz={gecersizIsle} />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-wait">
              Keşif henüz yapılmadı. Saha ekranından girilecek (Faz 4).
            </p>
          )}
        </Adim>

        <Adim
          k="izin"
          baslik="İş izni & malzeme"
          durum={durumlar.izin!}
          zaman={null}
          acik={acikAdim === 'izin'}
          onAc={() => setAcik(acikAdim === 'izin' ? '__kapali__' : 'izin')}
          onDuzelt={duzeltGate('izinMalzeme')}
        >
          {/* K4 — izin ve malzeme EŞ ZAMANLI, biri diğerini kilitlemez */}
          <div className="space-y-3 text-sm">
            {/*
              DIŞ PROJE (SOCAR, 09.10.2026). Cevap 5: *"Dış projeler için bu adım
              atlanabilir."* İzin bölümü hiç çizilmez; K5 kapısı da kâğıt istemez.
            */}
            {d.izinGerekmez ? (
              <p className="text-wait">Bu bölgede iş izni gerekmiyor (dış proje).</p>
            ) : (
            <div>
              {d.izin === 'alindi' && <span className="text-ok">✓ Müşteri iş izni alındı</span>}
              {/* İmzalı izin kâğıdı — onaylayan kişi kâğıda BAKABİLMELİ */}
              {foto('izin').length > 0 && (
                <div className="mt-1.5">
                  <FotoSerit
                    fotolar={foto('izin')}
                    onGecersiz={gecersizIsle}
                    eylem="yanlışsa değiştir"
                  />
                </div>
              )}
              {/* Ertelenen fotoğrafın sonradan yüklenme yolu — teslim buna bağlı (K5). */}
              {izinFotosuEksik && d.durum !== 'kapali' && (
                <div className="mt-2 rounded-(--radius-mod) border border-warn/40 bg-warn/5 p-3">
                  <p className="mb-2 text-xs text-ink-2">
                    Kâğıdın fotoğrafı yok — <strong>iş teslim edilemez</strong> (K5).
                  </p>
                  <FotoEkle
                    jobId={d.id}
                    step="izin"
                    etiket="İzin kâğıdını yükle"
                    gorunum="kompakt"
                    onBitti={yukle}
                  />
                </div>
              )}
              {/*
                ⚠️ ÇOK SAYFALI İZİN (06.09.2026, Akçansa: bir izin BEŞ SAYFA).
                   Yukarıdaki kutu K5 kapısı kapanınca yok oluyor; ikinci sayfayı
                   yükleyecek yer kalmıyordu. Kapı bir sayfayla açılır, belge
                   bitmiş olmaz.
              */}
              {foto('izin').length > 0 && d.durum !== 'kapali' && (
                <div className="mt-2">
                  <FotoEkle
                    jobId={d.id}
                    step="izin"
                    etiket="Sayfa ekle"
                    gorunum="kompakt"
                    onBitti={yukle}
                  />
                </div>
              )}
              {d.izin === 'override' && (
                <span className="text-warn">
                  ⚠ İzinsiz başlatıldı — {d.izinNotu} <em>(sorumluluk kaydı)</em>
                </span>
              )}
              {d.izin == null && (
                <>
                  {/*
                    ⚠️ ÖNCEDEN BURADA YALNIZ "İzin alındı olarak işaretle" DÜĞMESİ
                       VARDI — fotoğrafsız, yani KANITSIZ kayıt üretiyordu. Mobilde
                       ise fotoğraf isteniyordu; aynı adım iki cihazda iki farklı
                       titizlikte çalışıyordu. İzin "müşteri imzalı kâğıt"tır (§3.5,
                       4 imza); kanıtı o kâğıdın fotoğrafıdır.

                       Bu, iş emri fotoğrafındaki hatanın aynısıydı: bayrak vardı,
                       dosya yoktu (31.07'de düzeltilmişti).
                  */}
                  <FotoEkle
                    jobId={d.id}
                    step="izin"
                    etiket="İmzalı izin kâğıdını yükle"
                    gorunum="kompakt"
                    onBitti={async () => {
                      await cagir('izin', { durum: 'alindi' });
                    }}
                  />
                  {/*
                    FOTOĞRAF ERTELENEBİLİR (müşteri kararı 03.08.2026): saha ekibi
                    kâğıdı sonra çekiyor, iş bunun için beklememeli. Kapı TESLİMDE
                    — "Onayla & Teslim" düğmesi kâğıt yüklenmeden açılmaz (K5).
                  */}
                  <button
                    onClick={() => void cagir('izin', { durum: 'alindi' })}
                    className="mt-2 block text-xs text-muted underline underline-offset-2 hover:text-navy"
                  >
                    İzin alındı, fotoğrafı sonra yükleyeceğim
                  </button>
                  {/* K5 — override BUTON DEĞİL, altta duran LİNK. Kazara basılmasın. */}
                  {!overrideAcik ? (
                    <button
                      onClick={() => setOverrideAcik(true)}
                      className="mt-2 block text-xs text-warn underline underline-offset-2"
                    >
                      İzni alamadım — yine de başlıyorum (kayıt düşülür)
                    </button>
                  ) : (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <input
                        className="min-w-56 flex-1 rounded-(--radius-mod) border border-warn px-3 py-1.5 text-sm"
                        placeholder="Neden izin alınamadı?"
                        value={overrideNot}
                        onChange={(e) => setOverrideNot(e.target.value)}
                      />
                      <button
                        disabled={mesgul || overrideNot.trim().length < 3}
                        onClick={() =>
                          void cagir('izin', { durum: 'override', overrideNotu: overrideNot })
                        }
                        className="rounded-(--radius-mod) bg-warn px-3 py-1.5 text-sm text-white disabled:opacity-40"
                      >
                        İzinsiz devam et — sorumluluk bende
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
            )}
            <div>
              {d.malzemeSevk ? (
                <span className="text-ok">✓ Malzeme hazır ve sevk edildi</span>
              ) : (
                <button
                  disabled={mesgul || d.durum === 'giris'}
                  onClick={() => void cagir('malzeme')}
                  className="rounded-(--radius-mod) border border-line-2 px-3 py-1.5 disabled:opacity-40"
                >
                  Malzeme sevk edildi
                </button>
              )}
            </div>
          </div>
        </Adim>

        <Adim
          k="kurulum"
          baslik="Kurulum"
          /* Kilitliyse NEDENİ yazsın — fotoğraf sanılıyordu, oysa şart değil. */
          alt={
            durumlar.kurulum === 'locked' ? 'Önce malzeme sevkini işaretle — izin şart değil' : undefined
          }
          durum={durumlar.kurulum!}
          zaman={kurulum?.basladiAt ?? null}
          acik={acikAdim === 'kurulum'}
          onAc={() => setAcik(acikAdim === 'kurulum' ? '__kapali__' : 'kurulum')}
          onDuzelt={duzeltGate('ekip')}
        >
          <div className="space-y-2 text-sm">
            {kurulum?.basladiAt && (
              <p className="num text-wait">Başlama: {zmn(kurulum.basladiAt)}</p>
            )}
            {kurulum?.bittiAt && <p className="num text-wait">Bitiş: {zmn(kurulum.bittiAt)}</p>}
            <p className="text-wait">
              Ekip: {kurulum?.ekip.join(', ') || '—'}
              {kurulum?.ekip.length ? ` (${kurulum.ekip.length} kişi)` : ''}
            </p>
            {/*
              ⚠️ `kesif` DE KABUL EDİLİYOR. Durum makinesi `kurulum_basladi`yı
                 hem `kesif` hem `hazirlik`ten kabul ediyor; izin cevapsız kalan
                 eski işler `hazirlik`e terfi etmemiş olabilir ve yalnız
                 `hazirlik`e bakmak o işlerde düğmeyi hiç göstermezdi.
            */}
            {(d.durum === 'hazirlik' || d.durum === 'kesif') && d.malzemeSevk && (
              <button
                disabled={mesgul}
                onClick={() => void cagir('kurulum-basla', { overrideOnayi: false })}
                className="rounded-(--radius-mod) bg-navy px-3 py-2 text-white disabled:opacity-40"
              >
                Kuruluma başla
              </button>
            )}
            {d.durum === 'kurulum' && (
              <button
                disabled={mesgul}
                onClick={() => void cagir('kurulum-bitti')}
                className="rounded-(--radius-mod) bg-navy px-3 py-2 text-white disabled:opacity-40"
              >
                Ben Kurdum
              </button>
            )}
          </div>
        </Adim>

        {/* Kontrol & Onay — v9'da ayrı sayfadan buraya taşındı (inline) */}
        <KontrolOnay
          d={d}
          mesgul={mesgul}
          durum={durumlar.onay!}
          acik={acikAdim === 'onay'}
          onAc={() => setAcik(acikAdim === 'onay' ? '__kapali__' : 'onay')}
          onYuklendi={yukle}
          onTeslim={(olculer, iskeleOnayNo) =>
            void cagir('teslim', { nihaiOlculer: olculer, iskeleOnayNo })
          }
          hata={hata}
          onGuvenliDegil={(gerekce) => void cagir('guvenli-degil', { gerekce })}
          onDuzelt={duzeltGate('nihaiOlcu')}
        />
      </section>

      {/*
        DİĞER FAZLAR — revize ve söküm, kurulumun ALTINDA (v9).

        ⚠️ 07.08.2026'da ÖZET KUTUSUNDAN ADIM AKORDEONUNA çevrildi. Önceki hâli
           yalnız ekip/not/başlama/bitiş yazıyordu; müşteri *"söküm işleri
           sipariş içinde kurulum gibi gözükmüyor"* dedi ve haklıydı — mobilde
           söküm ZATEN adım adım görünüyordu, ayrışan masaüstüydü.

        ⚠️ KAPALI BAŞLAR. Bir iskelenin ömründe birden çok faz olabiliyor
           (3 revize + 1 söküm mümkün); hepsi açık gelseydi iş detayı
           okunamaz hâle gelirdi. Başlık faz + durum + tarih taşıyor, ayrıntı
           isteyen açıyor.
      */}
      {d.fazlar
        .filter((f) => f.tip !== 'kurulum')
        .map((f) => (
          <FazKutusu key={f.id} faz={f} fotolar={d.fotolar} jobId={d.id} onBitti={yukle}
            sefYetkisi={d.yetki.sefYetkisi} />
        ))}

      {/*
        GÖRÜNÜRLÜK KURALI (Bölüm 6.3): bu üç aksiyon YALNIZ iskele `ayakta`
        iken görünür. Devam eden bir faz varken ikinci faz açılamaz.
      */}
      {d.durum === 'ayakta' && (
        <FazAksiyon jobId={d.id} oncekiEkip={kurulum?.ekip ?? []} onBitti={yukle} />
      )}

      {ben?.santiye.hakedisVerisi && d.durum !== 'iptal' && (
        <HakedisPaneli d={d} duzenleyebilir={d.yetki.sefYetkisi} onBitti={yukle} />
      )}

      {/* İZ KAYDI — K10: yalnız gerçekleşen adımlar, tarih VE saat */}
      <section className="rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) p-4">
        <h2 className="mb-3 text-sm font-semibold text-navy">İz Kaydı</h2>
        <ol className="space-y-2 text-sm">
          {d.izKaydi.map((a, i) => (
            <li key={i} className="flex gap-3">
              <span className="num shrink-0 text-wait">{zmn(a.zaman)}</span>
              <span className="text-ink-2">
                {a.metin}
                {a.temsilMi && (
                  <span className="ml-2 rounded bg-warn/15 px-1.5 py-0.5 text-xs text-warn">
                    kullanıcı geçişi altında
                  </span>
                )}
              </span>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

/**
 * REVİZE KONTROL & ONAY — K1: kuran onaylayamaz, şef verir.
 *
 * ⚠️ Revize sonrası boyut DEĞİŞMİŞ olabilir (yükseltme/alçaltma). Değiştiyse
 *    yeni metraj girilir ve iskele tekrar `ayakta` olur.
 *
 * ⚠️ BİLİNEN SINIR: burada tek sayı alınıyor ve m³ tarafına yazılıyor. Çift
 *    birime geçişte (02.08.2026) revize akışı dışarıda kaldı; revize çoğunlukla
 *    yükseltme/alçaltma yani hacimsel olduğu için şimdilik makul. Tam doğrusu
 *    ölçü satırlarına geçmesi — 4. tur 3. sorunun cevabıyla birlikte yapılacak.
 */
function RevizeOnay({ jobId, onBitti }: { jobId: string; onBitti: () => void | Promise<void> }) {
  const [degisti, setDegisti] = useState(false);
  const [yeni, setYeni] = useState('');
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState<string | null>(null);

  async function onayla() {
    setMesgul(true);
    setHata(null);
    try {
      await api.post(`/isler/${jobId}/revize-onay`, {
        clientCommandId: crypto.randomUUID(),
        olayZamani: new Date().toISOString(),
        yeniMetraj: degisti && yeni ? Number(yeni.replace(',', '.')) : null,
      });
      await onBitti();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Onaylanamadı');
      setMesgul(false);
    }
  }

  return (
    <div className="mt-2 rounded-(--radius-mod) border border-stok/40 bg-white p-3">
      <p className="mb-2 text-sm font-medium text-ink-2">Revize kontrol &amp; onayı</p>
      <label className="mb-2 flex items-center gap-2 text-sm text-ink-2">
        <input type="checkbox" checked={degisti} onChange={(e) => setDegisti(e.target.checked)} />
        Boyut değişti
      </label>
      {degisti && (
        <input
          className="num mb-2 w-40 rounded-(--radius-mod) border border-line-2 px-3 py-1.5 text-sm"
          inputMode="decimal"
          placeholder="yeni m³"
          value={yeni}
          onChange={(e) => setYeni(e.target.value)}
        />
      )}
      {hata && <p className="mb-2 text-xs text-crit">{hata}</p>}
      <button
        disabled={mesgul || (degisti && !yeni)}
        onClick={() => void onayla()}
        className="w-full rounded-(--radius-mod) bg-ok px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
      >
        {mesgul ? 'Onaylanıyor…' : 'Revizeyi onayla → iskele tekrar KURULU'}
      </button>
    </div>
  );
}

/**
 * Adim durumu — PROTOTIPTEN (onayli referans): dort durum var, iki degil.
 *   done    : tamamlandi
 *   current : SIRADAKI adim — otomatik ACIK gelir
 *   locked  : sirasi gelmedi, TIKLANAMAZ (asma kilit)
 * `gate` ayrica isaretlenir: sert gecit olan adim gorsel olarak ayrisir.
 */
export type AdimDurum = 'done' | 'current' | 'locked';

function Adim({
  baslik,
  alt,
  durum,
  gate,
  zaman,
  acik,
  onAc,
  onDuzelt,
  children,
}: {
  k: string;
  baslik: string;
  /** Kilitli adımda NEDENİ yazar — kullanıcı asma kilide bakıp tahmin etmesin. */
  alt?: string;
  durum: AdimDurum;
  gate?: boolean;
  zaman: string | null;
  acik: boolean;
  onAc: () => void;
  /** ⋮ — şefin geçmişe dönük düzeltmesi (§6.3). Verilmezse düğme çizilmez. */
  onDuzelt?: () => void;
  children: React.ReactNode;
}) {
  const kilitli = durum === 'locked';
  return (
    <div
      className={`border-b border-line/70 last:border-0 ${
        gate ? 'border-l-2 border-l-crit' : ''
      }`}
    >
      <button
        onClick={kilitli ? undefined : onAc}
        disabled={kilitli}
        className={`flex w-full items-center gap-3 px-4 py-3 text-left ${
          kilitli ? 'cursor-default opacity-50' : 'hover:bg-ground'
        }`}
      >
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-(--radius-mod-sm) ${
            durum === 'done'
              ? 'bg-ok text-white'
              : kilitli
                ? 'bg-line text-muted'
                : gate
                  ? 'bg-crit-bg text-crit'
                  : 'bg-navy-bg text-navy'
          }`}
        >
          {durum === 'done' ? (
            <OnayIkon boyut={15} />
          ) : kilitli ? (
            <KilitIkon boyut={15} />
          ) : (
            <NoktaIkon boyut={15} />
          )}
        </span>
        <span className="flex-1 text-sm text-ink">
          {baslik}
          {gate && <span className="ml-2 text-xs text-crit">sert geçit</span>}
          {alt && <span className="mt-0.5 block text-xs text-wait">{alt}</span>}
        </span>
        {/* K10 — zaman damgası YALNIZ gerçekleşmiş adımlarda */}
        {zaman && <span className="num text-xs text-wait">{zmn(zaman)}</span>}
        {/* ⋮ — §6.3: şefin geçmişe dönük düzeltmesi. Akordeonu açmaz. */}
        {onDuzelt && (
          <span
            role="button"
            tabIndex={0}
            title="Düzelt (şef)"
            onClick={(e) => {
              e.stopPropagation();
              onDuzelt();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                onDuzelt();
              }
            }}
            className="rounded px-1.5 text-wait hover:bg-neutral-200 hover:text-navy"
          >
            ⋮
          </span>
        )}
      </button>
      {acik && !kilitli && <div className="px-4 pb-4 pl-11">{children}</div>}
    </div>
  );
}

/** TEK SERT GEÇİT — K1 (onaylayan ∉ kuran) + K2 (3 foto) + K3 (iş emri fotosu). */
function KontrolOnay({
  d,
  durum,
  mesgul,
  acik,
  onAc,
  onYuklendi,
  onTeslim,
  onGuvenliDegil,
  onDuzelt,
  hata,
}: {
  d: Detay;
  durum: AdimDurum;
  mesgul: boolean;
  acik: boolean;
  onAc: () => void;
  onYuklendi: () => void | Promise<void>;
  /** Teslime gidecek NİHAİ ölçü satırları — keşifin aynısı ya da elle düzenlenmiş hâli. */
  onTeslim: (olculer: OlcuInput[], iskeleOnayNo: string) => void;
  onGuvenliDegil: (gerekce: string) => void;
  /** Sunucudan dönen son hata — teslim düğmesinin YANINDA gösterilir. */
  hata: string | null;
  /** ⋮ — teslim SONRASI nihai ölçü/onay no düzeltmesi. Verilmezse çizilmez. */
  onDuzelt?: () => void;
}) {
  const [gerekce, setGerekce] = useState('');
  /** Onay no müşteriden ONAYLA BİRLİKTE gelir; kayıtlıysa önden dolu. */
  const [onayNo, setOnayNo] = useState(d.iskeleOnayNo ?? '');
  /** `null` → keşif ölçüsü aynen (varsayılan). Dolu → şef toplamı elle yazdı. */
  const [manuel, setManuel] = useState<ManuelOlcu[] | null>(null);
  /** Üstteki "Nihai boyut" kutusu bundan beslenir — seçimle ANINDA değişir. */
  const nihai = nihaiToplam(d.olculer, manuel);
  const foto = (s: string) => d.fotolar.filter((f) => f.step === s);

  /*
   * ⚠️ KONTROL FORMU ZORUNLU DEĞİL (kullanıcı kararı 04.08.2026).
   *    Yuva duruyor — form yüklenebilir, arşivde ve kanıt zincirinde yerini
   *    korur (K8) — ama teslimi BLOKLAMAZ. Yeşil kart ve kurulu iskele sert
   *    geçit olmaya devam ediyor. Kural `@mod/domain`de; burası onu yeniden
   *    yazmıyor, yalnız aynı ölçütle yuvayı işaretliyor.
   */
  const yuvalar = [
    { step: 'kontrol_formu', ad: 'Kontrol formu', zorunlu: false },
    { step: 'yesil_kart', ad: 'Yeşil kart', zorunlu: true },
    { step: 'kurulu_iskele', ad: 'Kurulu iskele', zorunlu: true },
  ];
  const fotolarTam = yuvalar.every((y) => !y.zorunlu || foto(y.step).length > 0);
  const teslimEdildi = d.teslimAt != null;
  /*
   * K5 — kural `@mod/domain`den, BURADA YENİDEN YAZILMIYOR.
   *
   * ⚠️ Bir kez yazılmıştı (`izin === 'alindi' && foto yok`) ve kural sunucuda
   *    genişleyince (cevapsızlık da reddedilir oldu) ikisi AYRIŞTI: düğme açık
   *    görünüyor, sunucu reddediyordu — kullanıcıya hiçbir sebep gösterilmeden.
   */
  const izinFotosuEksik = !k5_izinFotosuVarMi(izinDilimi(d)).ok;
  /*
   * K5 — "izin alındı" işaretliyse imzalı kâğıdın fotoğrafı TESLİMİN ön koşulu.
   * İzin adımında ertelenebilir; kapı burada, çünkü kâğıda ulaşabilen kişi
   * (şef/ofis) burada. Sunucu da reddediyor — bu yalnız sebebi gösteriyor.
   */

  /*
   * TESLİM ENGELLERİ — TEK KAYNAK.
   *
   * ⚠️ Düğmenin `disabled` koşulu ile altındaki gerekçe metni AYRI YAZILMIŞTI ve
   *    AYRIŞTILAR: `olculer.length === 0` düğmeyi söndürüyor ama gerekçe
   *    listesinde yoktu. Sahada karşılığı şuydu — fotoğrafların üçü de yüklü,
   *    onay no girilmiş, düğme ölü ve ekranda TEK KELİME açıklama yok
   *    (Bedirhan, 04.08.2026). Kullanıcı da haklı olarak "onaylayamıyorum" dedi.
   *
   *    Artık ikisi de bu diziden besleniyor; yeni bir koşul eklemek gerekçesini
   *    yazmaya MECBUR bırakır.
   */
  const teslimEngelleri: string[] = [
    !fotolarTam ? 'Yeşil kart ve kurulu iskele fotoğrafı eklenmeden teslim edilemez (K2).' : null,
    !d.isEmriFotoVar ? 'İş emri kâğıdı fotoğrafı eksik (K3).' : null,
    izinFotosuEksik ? 'İmzalı izin kâğıdı fotoğrafı eksik (K5).' : null,
    !onayNo.trim() ? 'İskele onay no girilmeli.' : null,
    /*
     * ⚠️ ENGEL ARTIK SATIR SAYISINA DEĞİL, TOPLAMA BAKIYOR. Elle giriş geri
     *    geldiğinde (05.08.2026) "satır yoksa teslim edilemez" kuralı yanlış
     *    olurdu: şef toplamı elle yazmışsa satıra gerek yok. Sunucudaki
     *    `teslimSchema` refine'ı ile AYNI ölçüt — ikisi ayrışırsa düğme açık
     *    görünüp sunucu reddeder, bu ekranda daha önce yaşandı (8960edd).
     */
    nihai.m3 <= 0 && nihai.m2 <= 0
      ? manuel
        ? 'Elle girilen metraj sıfır olamaz — m³ ya da m² alanlarından biri dolmalı.'
        : 'Nihai ölçü yok — teslim için en az bir ölçü satırı gerekli. Yukarıdaki ⋮ → keşif ile ekleyin (ya da metrajı elle girin).'
      : null,
  ].filter((x): x is string => x !== null);

  return (
    <div className="border-b border-l-2 border-l-crit border-line/70 last:border-0">
      <button
        onClick={durum === 'locked' ? undefined : onAc}
        disabled={durum === 'locked'}
        className={`flex w-full items-center gap-3 px-4 py-3 text-left ${
          durum === 'locked' ? 'cursor-default opacity-50' : 'hover:bg-ground'
        }`}
      >
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-(--radius-mod-sm) ${
            durum === 'done'
              ? 'bg-ok text-white'
              : durum === 'locked'
                ? 'bg-line text-muted'
                : 'bg-crit-bg text-crit'
          }`}
        >
          {durum === 'done' ? (
            <OnayIkon boyut={15} />
          ) : durum === 'locked' ? (
            <KilitIkon boyut={15} />
          ) : (
            <NoktaIkon boyut={15} />
          )}
        </span>
        <span className="flex-1 text-sm font-medium text-ink">
          Kontrol &amp; Onay + Teslim
          <span className="ml-2 text-xs text-crit">tek sert geçit</span>
        </span>
        {!teslimEdildi && d.durum === 'kontrol' && (
          <span className="rounded bg-info/15 px-2 py-0.5 text-xs text-info">şefte</span>
        )}
        {/*
          ⋮ akordeon düğmesinin İÇİNDE — iç içe <button> geçersiz HTML.
          `span` + stopPropagation ile akordeonu açmadan düzeltme formunu açar.
        */}
        {onDuzelt && (
          <span
            role="button"
            tabIndex={0}
            title="Bu adımı düzelt"
            onClick={(e) => {
              e.stopPropagation();
              onDuzelt();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                e.preventDefault();
                onDuzelt();
              }
            }}
            className="cursor-pointer rounded px-1.5 py-0.5 text-lg leading-none text-wait hover:bg-line/40 hover:text-navy"
          >
            ⋮
          </span>
        )}
      </button>

      {acik && durum !== 'locked' && (
        <div className="space-y-4 px-4 pb-4 pl-11">
          {!d.yetki.sefYetkisi && (
            <p className="rounded-(--radius-mod) bg-surface-2 p-3 text-sm text-wait">
              Onayı şantiye şefi verir — <strong>kuran kişi kendi işini onaylayamaz</strong> (K1,
              regülasyon). Sizde işlem yok.
            </p>
          )}

          {/* 3 fotoğraf yuvası. Dijital checklist YOK — yasal form kâğıtta dolduruldu. */}
          <div className="grid grid-cols-3 gap-2">
            {yuvalar.map((y) => {
              const f = foto(y.step);
              return (
                <div
                  key={y.step}
                  className={`rounded-(--radius-mod) border p-3 text-center text-xs ${
                    f.length ? 'border-ok bg-ok/5 text-ok' : 'border-dashed border-line-2 text-wait'
                  }`}
                >
                  <p className="mb-1.5 font-medium">
                    {y.ad}
                    {!y.zorunlu && (
                      <span className="ml-1 font-normal text-wait">(opsiyonel)</span>
                    )}
                  </p>
                  {f.length > 0 && (
                    <div className="mb-1.5">
                      <FotoSerit fotolar={f} />
                    </div>
                  )}
                  {/*
                    K2 — yeşil kart ve kurulu iskele teslim için ZORUNLU;
                    kontrol formu 04.08.2026'da zorunluluktan çıkarıldı ama
                    yuvası duruyor (yüklenirse kanıt zincirine girer).
                    "Kurulu iskele" ÇOKLU olabilir (2–3 açı), diğerleri tek
                    fotoğraf; ama tekrar yüklemeyi engellemiyoruz — yanlış
                    fotoğraf çekilirse şefin ikinci bir yol araması gerekirdi.
                  */}
                  {d.yetki.sefYetkisi && !teslimEdildi && (
                    <FotoEkle
                      jobId={d.id}
                      step={y.step}
                      etiket={f.length ? 'bir tane daha' : 'yükle'}
                      gorunum="kompakt"
                      onBitti={onYuklendi}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {izinFotosuEksik && !teslimEdildi && (
            <div className="rounded-(--radius-mod) border border-warn/40 bg-warn/5 p-3">
              <p className="mb-2 text-sm text-navy">
                <strong>İmzalı izin kâğıdının fotoğrafı eksik.</strong>{' '}
                {d.izin === 'alindi'
                  ? 'İzin adımında “alındı” işaretlenmiş ama kâğıt yüklenmemiş'
                  : 'İzin adımı hiç cevaplanmamış'}{' '}
                — <strong>teslim bundan önce yapılamaz</strong> (K5). Kâğıt elinizdeyse şimdi
                yükleyin; hiç alınamadıysa izin adımından{' '}
                <em>“izni alamadım”</em> kaydını düşün.
              </p>
              <FotoEkle
                jobId={d.id}
                step="izin"
                etiket="İzin kâğıdını yükle"
                gorunum="kompakt"
                onBitti={onYuklendi}
              />
            </div>
          )}

          {d.yetki.sefYetkisi && !teslimEdildi && (
            <>
              {/*
                NİHAİ BOYUT — artık tek sayı DEĞİL, ölçü satırları.
                Şef keşif ölçülerini aynen kabul eder ya da düzeltir; iki toplam
                (m³/m²) otomatik çıkar. Satır satır girilmiş bir şeyi tek sayıya
                sıkıştırıp o sayıyı elle düzelttirmek, hangi parçanın değiştiği
                bilgisini yok ediyordu (müşteri kararı 02.08.2026).
              */}
              <div className="rounded-(--radius-mod) border border-line bg-surface-2 p-3">
                <p className="mb-2 text-sm font-medium text-ink-2">Nihai boyut</p>
                {/*
                  ⚠️ Toplam KAYITLI değerden değil, SEÇİMDEN gelir (`nihai`).
                     Aşağıdaki seçici değiştiği anda burası güncellenir — teslime
                     gidecek sayı ile ekrandaki sayı aynı fonksiyondan çıkar.
                */}
                <OlcuListesi
                  olculer={d.olculer}
                  m3={nihai.m3}
                  m2={nihai.m2}
                  manuel={manuel !== null}
                />
                {/*
                  ⚠️ Metin, ölçü OLAN ve OLMAYAN işte farklı olmalı. Tek hâli
                  ("değiştiyse düzeltin") hiç ölçüsü olmayan işte kullanıcıya
                  ölçü EKLEMESİ gerektiğini söylemiyordu — teslim düğmesi de
                  sessizce kapalı olunca çıkmaz sokak oluyordu.
                */}
                {d.olculer.length === 0 && !manuel && (
                  <p className="mt-2 text-xs text-warn">
                    <strong>Henüz ölçü girilmemiş.</strong> Aşağıdan{' '}
                    <strong>⋮ → keşif</strong> ile ölçü ekleyin ya da metrajı{' '}
                    <strong>elle girin</strong>.
                  </p>
                )}
              </div>

              {/*
                NİHAİ METRAJ SEÇİCİSİ — İskele Onay No'nun ÜSTÜNDE (kullanıcı
                isteği 05.08.2026). Bileşen `lib/olcu.tsx`'te ve mobil ekranla
                ORTAK; iki ekranın ayrışması bu projede daha önce sessiz metraj
                kaybına yol açtı.
              */}
              <NihaiMetrajSecici olculer={d.olculer} manuel={manuel} onDegis={setManuel} />

              {/*
                İSKELE ONAY NO — iş açma adımından BURAYA taşındı (03.08.2026).
                Numara iş açılırken henüz yoktur: müşteri iskeleyi görüp
                onaylayınca veriyor. Açılışta sormak, sahada her zaman boş
                bırakılan bir alan üretiyordu.
              */}
              <div>
                <label className="mb-1 block text-sm font-medium text-ink-2" htmlFor="onayNo">
                  İskele Onay No <span className="font-normal text-crit">(zorunlu)</span>
                </label>
                <input
                  id="onayNo"
                  className="num w-48 rounded-(--radius-mod) border border-line-2 px-3 py-1.5 text-sm outline-none focus:border-navy"
                  placeholder="—"
                  value={onayNo}
                  onChange={(e) => setOnayNo(e.target.value)}
                />
              </div>

              <button
                disabled={mesgul || teslimEngelleri.length > 0}
                onClick={() => onTeslim(nihaiOlculer(d.olculer, manuel), onayNo.trim())}
                className="w-full rounded-(--radius-mod) bg-ok px-4 py-2.5 text-sm font-medium text-white disabled:opacity-40"
              >
                Onayla &amp; Teslim
              </button>
              {teslimEngelleri.length > 0 && (
                <ul className="space-y-0.5 text-xs text-warn">
                  {teslimEngelleri.map((e) => (
                    <li key={e}>• {e}</li>
                  ))}
                </ul>
              )}
              {/*
                HATA BURADA DA GÖSTERİLİR. Sayfanın en üstünde bir hata şeridi
                var ama şef teslim panelindeyken EKRAN DIŞINDA kalıyor: düğmeye
                basıyor, hiçbir şey olmuyor sanıyor. Sunucunun reddi eylemin
                yanında görünmeli.
              */}
              {hata && (
                <p className="rounded-(--radius-mod) border border-crit/40 bg-crit/10 p-3 text-sm text-crit">
                  {hata}
                </p>
              )}

              {/* Güvenlik döngüsü */}
              <div className="border-t border-line/70 pt-3">
                <input
                  className="mb-2 w-full rounded-(--radius-mod) border border-line-2 px-3 py-1.5 text-sm"
                  placeholder="Güvenli değilse gerekçe…"
                  value={gerekce}
                  onChange={(e) => setGerekce(e.target.value)}
                />
                <button
                  disabled={mesgul || gerekce.trim().length < 3}
                  onClick={() => onGuvenliDegil(gerekce)}
                  className="w-full rounded-(--radius-mod) border border-crit px-4 py-2 text-sm text-crit disabled:opacity-40"
                >
                  Güvenli değil → kuruluma geri gönder
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * FAZ KUTUSU — söküm/revize, adım adım.
 *
 * ⚠️ SALT GÖRÜNÜM (bir istisnayla). Söküm işini saha ekibi TELEFONDAN yürütür;
 *    masaüstünden "söküme başladım" tiki atılabilseydi, sahada olmayan bir
 *    olay kaydedilirdi. Buradaki tek eylem revize onayı — o zaten şefin işi
 *    ve K1 gereği masaüstünde yapılması doğru.
 *
 * ⚠️ Adım tanımı `lib/faz-adimlari.ts`'ten geliyor; mobil ekran da aynı
 *    kaynaktan besleniyor. Kopyalansaydı biri güncellenip diğeri unutulurdu.
 */
function FazKutusu({
  faz,
  fotolar,
  jobId,
  onBitti,
  sefYetkisi,
}: {
  faz: Detay['fazlar'][number];
  fotolar: Detay['fotolar'];
  jobId: string;
  onBitti: () => void;
  sefYetkisi: boolean;
}) {
  // Kapalı başlar — bkz. çağıran taraftaki gerekçe.
  const [acik, setAcik] = useState(false);
  const [acikAdim, setAcikAdim] = useState<string | null>(null);

  const revizeMi = faz.tip === 'revize';
  const adimlar = fazAdimlari(faz.tip);
  const sira = fazSirasi(revizeMi ? 'revize' : 'sokum', faz.durum);
  const bitti = faz.durum === 'onay' || faz.durum === 'iade';

  return (
    <section className="mb-5 overflow-hidden rounded-(--radius-mod) border border-stok/40 bg-white">
      <button
        onClick={() => setAcik((v) => !v)}
        className="flex w-full items-center gap-3 border-b border-stok/20 bg-stok/5 px-4 py-2.5 text-left hover:bg-stok/10"
      >
        <span className="text-sm font-semibold text-stok">
          {revizeMi ? 'REVİZYON' : 'SÖKÜM'}
          {faz.revizeTur && <span className="ml-2 font-normal">· {faz.revizeTur}</span>}
        </span>
        <span className="text-xs text-wait">
          {faz.durum === SOKUM_GERI_ALINDI ? 'geri alındı' : bitti ? 'tamamlandı' : (faz.durum ?? 'atandı')}
          {faz.basladiAt && ` · ${zmn(faz.basladiAt)}`}
          {faz.bittiAt && ` → ${zmn(faz.bittiAt)}`}
        </span>
        <span className="ml-auto text-xs text-wait">
          {sira}/{adimlar.length} adım {acik ? '▴' : '▾'}
        </span>
      </button>

      {acik && (
        <div className="px-4 py-3">
          <p className="mb-2 text-xs text-wait">Ekip: {faz.ekip.join(', ') || '—'}</p>
          {faz.not && (
            <p className="mb-2 text-sm text-ink-2">
              <NotIkon boyut={12} className="inline" /> {faz.not}
            </p>
          )}

          <ol className="space-y-1.5">
            {adimlar.map((a, i) => {
              const durum = adimDurumu(i, sira);
              const adimFoto = a.foto ? fotolar.filter((f) => f.step === a.foto) : [];
              const adimAcik = acikAdim === a.anahtar;
              return (
                <li
                  key={a.anahtar}
                  className={`rounded-(--radius-mod) border ${
                    durum === 'done'
                      ? 'border-ok-line bg-ok-bg'
                      : durum === 'current'
                        ? 'border-stok/40 bg-stok/5'
                        : 'border-line bg-surface-2 opacity-60'
                  }`}
                >
                  <button
                    onClick={() => setAcikAdim(adimAcik ? null : a.anahtar)}
                    disabled={durum === 'locked'}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left disabled:cursor-default"
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-(--radius-mod-sm) ${
                        durum === 'done'
                          ? 'bg-ok text-white'
                          : durum === 'current'
                            ? 'bg-stok/20 text-stok'
                            : 'bg-line text-muted'
                      }`}
                    >
                      {durum === 'done' ? (
                        <OnayIkon boyut={12} />
                      ) : durum === 'locked' ? (
                        <KilitIkon boyut={12} />
                      ) : (
                        <NoktaIkon boyut={12} />
                      )}
                    </span>
                    <span className="flex-1 text-sm text-ink">{a.baslik}</span>
                    {/* Fotoğraf SAYISI başlıkta: açmadan da var/yok görülür. */}
                    {a.foto && (
                      <span
                        className={`num text-xs ${adimFoto.length ? 'text-ok' : 'text-wait'}`}
                      >
                        {adimFoto.length} foto
                      </span>
                    )}
                  </button>

                  {adimAcik && durum !== 'locked' && (
                    <div className="border-t border-dashed border-line px-3 py-2.5">
                      {a.aciklama && <p className="mb-2 text-xs text-wait">{a.aciklama}</p>}
                      {a.foto ? (
                        adimFoto.length > 0 ? (
                          <div className="flex justify-start">
                            <FotoSerit fotolar={adimFoto} />
                          </div>
                        ) : (
                          <p className="text-xs text-wait">Bu adımda fotoğraf yok.</p>
                        )
                      ) : (
                        <p className="text-xs text-wait">Bu adım fotoğraf üretmez.</p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>

          {/*
            REVİZE ONAYI — K1: yalnız şef. Bu ekran EKSİKTİ: revize bitirilince
            şefin aksiyon kuyruğunda görünüyor ama onaylayacak bir düğme yoktu.
          */}
          {revizeMi && faz.durum === 'bitti' && sefYetkisi && (
            <div className="mt-3">
              <RevizeOnay jobId={jobId} onBitti={onBitti} />
            </div>
          )}
        </div>
      )}
    </section>
  );
}


/**
 * ŞEFİN DÜZELTMESİ — ⋮ ile açılır (§6.3, Faz 7).
 *
 * Doküman gerekçesi birebir: *"eksik iş emri kâğıdı, yanlış izin, karışık foto"*.
 *
 * ⚠️ GEREKÇE ZORUNLU. Gerekçesiz bir düzeltme denetimde HATADAN ayırt edilemez;
 *    iz kaydı değişmez (T4) ama NEDEN'i de taşımalı.
 *
 * ⚠️ Bu form durum DEĞİŞTİRMEZ — yalnız veriyi onarır. Akışı ilerletseydi
 *    K1/K2/K3 geçitlerini atlayan bir arka kapı olurdu.
 */
/**
 * İşi ERTELE veya İPTAL et — §3.5'teki "yapılmama nedeni" kutularının karşılığı.
 *
 * ⚠️ İkisi AYRI durum: erteleme "sonra yapacağız", iptal "yapmayacağız".
 *    Tek durumda toplamak, panodaki "bu iş neden bekliyor" sorusunu
 *    cevaplanamaz hâle getirirdi (30.07.2026 müşteri isteği).
 */
/**
 * SÖKÜM EMRİNİ GERİ AL — iskele yerinde kalır, iş `ayakta`ya döner.
 *
 * Nuh 26.08.2026: *"yanlışlıkla başlatılan veya karıştırılan bir söküm işinin
 * iptal edilmesi mümkün olmuyor."*
 *
 * ⚠️ `Ertele` bileşeni YENİDEN KULLANILMADI. O, iş emrinin arka yüzündeki
 *    "yapılmama nedeni" listesine bağlı; söküm emrini geri almanın o listeyle
 *    ilgisi yok. Zorlasaydık kullanıcıya anlamsız bir sebep seçtirirdik.
 *
 * ⚠️ Gerekçe ZORUNLU ve "emin misiniz?" var — söküm emri sahaya iş çıkarmıştır.
 *    İptal akışındaki iki kapının aynısı.
 */
function SokumGeriAl({
  id,
  onKapat,
  onBitti,
}: {
  id: string;
  onKapat: () => void;
  onBitti: () => void;
}) {
  const [gerekce, setGerekce] = useState('');
  const [eminMi, setEminMi] = useState(false);
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const eksik = gerekce.trim().length < 3;

  async function gonder() {
    setMesgul(true);
    setHata(null);
    try {
      await api.post(`/isler/${id}/sokum-geri-al`, {
        clientCommandId: crypto.randomUUID(),
        olayZamani: new Date().toISOString(),
        gerekce: gerekce.trim(),
      });
      onBitti();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'İşlem başarısız');
      setMesgul(false);
    }
  }

  const gir =
    'w-full rounded-(--radius-mod) border border-line-2 px-3 py-2 text-sm outline-none focus:border-navy';

  return (
    <div className="mb-4 rounded-(--radius-kart) border border-crit-line bg-crit-bg p-4">
      <h3 className="mb-1 text-sm font-semibold text-crit">Söküm emrini geri al</h3>
      <p className="mb-3 text-xs leading-relaxed text-wait">
        Söküm yanlışlıkla açıldıysa ya da başka bir iskeleyle karıştırıldıysa emir geri alınır:{' '}
        <strong>iskele yerinde kalır</strong> ve iş “kurulu” durumuna döner. Bu bir iş iptali
        değildir. Boş alan fotoğrafı çekildiyse iskele fiilen inmiştir ve geri alma reddedilir.
      </p>
      <input
        className={gir}
        value={gerekce}
        placeholder="Geri alma gerekçesi (zorunlu)"
        onChange={(e) => {
          setGerekce(e.target.value);
          setEminMi(false);
        }}
      />
      {eksik && gerekce.length > 0 && (
        <p className="mt-2 text-xs text-crit">Gerekçe en az 3 karakter — iz kaydına işlenir.</p>
      )}
      {hata && <p className="mt-2 text-sm text-crit">{hata}</p>}

      {eminMi && !eksik ? (
        <div className="mt-3 rounded-(--radius-mod) border border-crit-line bg-crit/5 p-3">
          <p className="mb-2 text-sm font-medium text-crit">
            Emin misiniz? Söküm emri geri alınacak, iş “kurulu” durumuna dönecek.
          </p>
          <div className="flex gap-2">
            <button disabled={mesgul} onClick={() => void gonder()} className="rounded-(--radius-mod) bg-crit px-4 py-2 text-sm font-medium text-white disabled:opacity-40">
              Evet, geri al
            </button>
            <SessizDugme onClick={() => setEminMi(false)}>Vazgeç</SessizDugme>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex gap-2">
          <button disabled={mesgul || eksik} onClick={() => setEminMi(true)} className="rounded-(--radius-mod) bg-crit px-4 py-2 text-sm font-medium text-white disabled:opacity-40">
            Söküm emrini geri al
          </button>
          <SessizDugme onClick={onKapat}>Vazgeç</SessizDugme>
        </div>
      )}
    </div>
  );
}

function Ertele({
  id,
  mod,
  onKapat,
  onBitti,
}: {
  id: string;
  mod: 'ertele' | 'iptal';
  onKapat: () => void;
  onBitti: () => void;
}) {
  const iptalMi = mod === 'iptal';
  const [neden, setNeden] = useState<string>('isletme_kaynakli');
  const [aciklama, setAciklama] = useState('');
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  /*
   * İPTALDE İKİ KAPI (müşteri isteği 04.08.2026):
   *   1. Gerekçe ZORUNLU — erteleme geri alınabilir bir bekleme, iptal ise
   *      "bu iş yapılmayacak" beyanıdır; sebebi kayda geçmeden verilmemeli.
   *   2. "Emin misiniz?" — kazara tıklamaya karşı. Ertelemede ikisi de yok,
   *      çünkü erteleme günlük ve zararsız bir işlem.
   */
  const [eminMi, setEminMi] = useState(false);
  const gerekceEksik = iptalMi && aciklama.trim().length < 3;

  async function gonder() {
    setMesgul(true);
    setHata(null);
    try {
      await api.post(`/isler/${id}/${iptalMi ? 'iptal' : 'ertele'}`, {
        clientCommandId: crypto.randomUUID(),
        olayZamani: new Date().toISOString(),
        neden,
        aciklama: aciklama.trim() || null,
      });
      onBitti();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'İşlem başarısız');
      setMesgul(false);
    }
  }

  const gir =
    'w-full rounded-(--radius-mod) border border-line-2 px-3 py-2 text-sm outline-none focus:border-navy';

  return (
    <div
      className={`mb-4 rounded-(--radius-kart) border p-4 ${
        iptalMi ? 'border-crit-line bg-crit-bg' : 'border-warn-line bg-warn-bg'
      }`}
    >
      <h3 className={`mb-1 text-sm font-semibold ${iptalMi ? 'text-crit' : 'text-warn'}`}>
        {iptalMi ? 'İşi iptal et' : 'İşi ertele'}
      </h3>
      <p className="mb-3 text-xs leading-relaxed text-wait">
        Kâğıt iş emrinin arka yüzündeki “yapılmama nedeni” kutuları.
        {iptalMi
          ? ' İptal edilen iş bir daha yapılmayacak demektir; yanlışlıkla iptal edilirse yeniden akışa alınabilir.'
          : ' Ertelenen iş sonra yapılacak demektir.'}{' '}
        İkisi de panoda <strong>dikkat kartı üretmez</strong> — sebebi bilinen bir bekleme,
        “bugün neye bakmalıyım” listesini kirletmemeli.
      </p>
      <select className={`${gir} mb-2`} value={neden} onChange={(e) => setNeden(e.target.value)}>
        {NEDENLER.map((n) => (
          <option key={n.d} value={n.d}>
            {n.e}
          </option>
        ))}
      </select>
      <input
        className={gir}
        value={aciklama}
        placeholder={iptalMi ? 'İptal gerekçesi (zorunlu)' : 'Açıklama (opsiyonel)'}
        onChange={(e) => {
          setAciklama(e.target.value);
          setEminMi(false); // gerekçe değişirse onay baştan alınır
        }}
      />
      {gerekceEksik && (
        <p className="mt-2 text-xs text-crit">
          İptal gerekçesi zorunludur (en az 3 karakter) — iz kaydına işlenir.
        </p>
      )}
      {hata && <p className="mt-2 text-sm text-crit">{hata}</p>}

      {/*
        EMİN MİSİNİZ — iptal geri alınabilir ("yeniden akışa al") ama panodan
        düşen, müşteriye "yapılmayacak" diyen bir karardır; tek tıkla verilmemeli.
        Onay, düğmenin YERİNE geçer: kazara basılan şey artık iptal değil, soru.
      */}
      {iptalMi && eminMi && !gerekceEksik ? (
        <div className="mt-3 rounded-(--radius-mod) border border-crit-line bg-crit/5 p-3">
          <p className="mb-2 text-sm font-medium text-crit">
            Bu iş iptal edilecek. Emin misiniz?
          </p>
          <p className="mb-3 text-xs text-wait">
            İş listeden <strong>silinmez</strong>; “iptal edildi” olarak görünmeye devam eder ve
            gerekirse yeniden akışa alınabilir.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => void gonder()}
              disabled={mesgul}
              className="rounded-(--radius-mod) bg-crit px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              {mesgul ? 'İşleniyor…' : 'Evet, iptal et'}
            </button>
            <SessizDugme onClick={() => setEminMi(false)}>Hayır, vazgeç</SessizDugme>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={() => (iptalMi ? setEminMi(true) : void gonder())}
            disabled={mesgul || gerekceEksik}
            className={`rounded-(--radius-mod) px-4 py-2 text-sm font-medium text-white disabled:opacity-40 ${
              iptalMi ? 'bg-crit' : 'bg-warn'
            }`}
          >
            {mesgul ? 'İşleniyor…' : iptalMi ? 'İptal et' : 'Ertele'}
          </button>
          <SessizDugme onClick={onKapat}>Vazgeç</SessizDugme>
        </div>
      )}
    </div>
  );
}

const HAKEDIS_BIRIM_ETIKET = { m3: 'm³', m2: 'm²', insan_saat: 'insan-saat' } as const;

/**
 * HAKEDİŞ VERİSİ (SOCAR, 09.10.2026) — söküm talep tarihi, hakediş metrajı,
 * kesinti.
 *
 * ⚠️ YALNIZ VERİ, HESAP YOK (kullanıcı kararı). Devir dokümanı §9 hakedişi ayrı
 *    modül sayıyor; SOCAR bu sayıları kira hakedişi için sistemden alacak.
 *    Burada girilir, dışa aktarımda (Excel) sütun olarak çıkar. Kira günü ya
 *    da kesintili tutar gibi türetilmiş hiçbir şey ekranda gösterilmez.
 *
 * Kimin gireceği 14. sorunun cevabıyla değişebilir (inspektör); o zamana
 * kadar şef ve mühendis girer, diğerleri salt okur.
 */
function HakedisPaneli({
  d,
  duzenleyebilir,
  onBitti,
}: {
  d: Detay;
  duzenleyebilir: boolean;
  onBitti: () => void | Promise<void>;
}) {
  // Saklanan an Türkiye gününün başlangıcı; giriş kutusu gün ister.
  const gunu = (s: string | null | undefined) =>
    s ? new Date(new Date(s).getTime() + 3 * 3_600_000).toISOString().slice(0, 10) : '';
  const ilk = {
    talep: gunu(d.sokumTalepAt),
    metraj: d.hakedisMetraj != null ? String(d.hakedisMetraj).replace('.', ',') : '',
    birim: d.hakedisBirim ?? '',
    kesinti: d.kesintiYuzde != null ? String(d.kesintiYuzde) : '',
  };
  const [talep, setTalep] = useState(ilk.talep);
  const [metraj, setMetraj] = useState(ilk.metraj);
  const [birim, setBirim] = useState(ilk.birim);
  const [kesinti, setKesinti] = useState(ilk.kesinti);
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);

  const metrajSayi = metraj.trim() ? Number(metraj.replace(',', '.')) : null;
  const kesintiSayi = kesinti.trim() ? Number(kesinti) : null;
  const gecersiz =
    (metrajSayi != null && (!Number.isFinite(metrajSayi) || metrajSayi < 0)) ||
    (kesintiSayi != null && (!Number.isInteger(kesintiSayi) || kesintiSayi < 1 || kesintiSayi > 99));
  const degisti =
    talep !== ilk.talep || metraj !== ilk.metraj || birim !== ilk.birim || kesinti !== ilk.kesinti;

  async function kaydet() {
    setMesgul(true);
    setHata(null);
    setBilgi(null);
    try {
      // Yalnız değişen alan gider: verilmeyen alana sunucu dokunmaz.
      const govde: Record<string, unknown> = {
        clientCommandId: crypto.randomUUID(),
        olayZamani: new Date().toISOString(),
      };
      if (talep !== ilk.talep) govde.sokumTalepTarihi = talep || null;
      if (metraj !== ilk.metraj) govde.hakedisMetraj = metrajSayi;
      if (birim !== ilk.birim) govde.hakedisBirim = birim || null;
      if (kesinti !== ilk.kesinti) govde.kesintiYuzde = kesintiSayi;
      await api.patch(`/isler/${d.id}/hakedis`, govde);
      setBilgi('Kaydedildi.');
      await onBitti();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Kaydedilemedi');
    } finally {
      setMesgul(false);
    }
  }

  const gir = 'rounded-(--radius-mod) border border-line-2 px-3 py-1.5 text-sm outline-none focus:border-navy disabled:bg-surface-2';
  return (
    <section className="mb-5 rounded-(--radius-mod) border border-line bg-surface p-4 shadow-(--shadow-mod)">
      <h2 className="mb-1 text-sm font-semibold text-navy">Hakediş verisi</h2>
      <p className="mb-3 text-xs text-wait">
        Yalnız kayıt içindir, sistem hesap yapmaz. Dışa aktarımda (Excel) sütun olarak çıkar.
      </p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <label className="block text-xs text-wait">
          Söküm talep tarihi
          <input
            type="date"
            className={`${gir} num mt-1 w-full`}
            value={talep}
            disabled={!duzenleyebilir}
            onChange={(e) => setTalep(e.target.value)}
          />
        </label>
        <label className="block text-xs text-wait">
          Hakediş metrajı
          <input
            className={`${gir} num mt-1 w-full`}
            inputMode="decimal"
            placeholder="—"
            value={metraj}
            disabled={!duzenleyebilir}
            onChange={(e) => setMetraj(e.target.value)}
          />
        </label>
        <label className="block text-xs text-wait">
          Birim
          <select
            className={`${gir} mt-1 w-full`}
            value={birim}
            disabled={!duzenleyebilir}
            onChange={(e) => setBirim(e.target.value)}
          >
            <option value="">—</option>
            {(Object.keys(HAKEDIS_BIRIM_ETIKET) as (keyof typeof HAKEDIS_BIRIM_ETIKET)[]).map((b) => (
              <option key={b} value={b}>
                {HAKEDIS_BIRIM_ETIKET[b]}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-wait">
          Kesinti %
          <input
            className={`${gir} num mt-1 w-full`}
            inputMode="numeric"
            placeholder="yok"
            value={kesinti}
            disabled={!duzenleyebilir}
            onChange={(e) => setKesinti(e.target.value)}
          />
        </label>
      </div>
      {duzenleyebilir && (
        <div className="mt-3 flex items-center gap-3">
          <button
            disabled={mesgul || !degisti || gecersiz}
            onClick={() => void kaydet()}
            className="rounded-(--radius-mod) bg-navy px-4 py-1.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {mesgul ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
          {gecersiz && (
            <span className="text-xs text-warn">
              Metraj sıfır ya da pozitif, kesinti 1 ile 99 arasında tam sayı olmalı.
            </span>
          )}
          {bilgi && <span className="text-xs text-ok">{bilgi}</span>}
          {hata && <span className="text-xs text-crit">{hata}</span>}
        </div>
      )}
    </section>
  );
}
